import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Geometry, Polygon } from 'ol/geom';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import { Fill, Stroke, Style } from 'ol/style';
import { addAlpha, setAlpha } from '@/utils/ColorHelper';
import { getFirstClusteredFeature } from '@/utils/SpatialHelper';
import {
    FeatureColorKey,
    FeatureDirection,
    FeatureRoadType,
    FeatureShowDirectionalArrows,
} from '../layers/FeatureLayerType';
import { selectedColor } from './Style';

function getLineStyle(
    highlight: boolean,
    selected: boolean,
    roadType: string,
    feature: Feature<Geometry>
): Style[] {
    const glowColor = addAlpha('#ffffff', 0.5);
    const color = selected
        ? selectedColor
        : getFirstClusteredFeature(feature).get(FeatureColorKey);
    const weights = {
        outerWidth: highlight || selected ? 10 : 7,
        innerWidth: highlight || selected ? 7 : 4,
        hairWidth: 5,
    };

    const style = {
        outer: new Style({
            fill: new Fill({
                color: addAlpha(color, 0.3),
            }),
            stroke: new Stroke({
                color: glowColor,
                width: weights.outerWidth,
            }),
            zIndex: 1,
        }),
        inner: new Style({
            stroke: new Stroke({
                color,
                width: weights.innerWidth,
            }),
            zIndex: 2,
        }),
        hair: new Style({
            stroke: new Stroke({
                color: '#ffffff',
                width: weights.hairWidth,
            }),
            zIndex: 3,
        }),
    };

    switch (roadType) {
        case 'Gang- og sykkelveg':
        case 'Gågate':
        case 'Sykkelveg':
            configureStroke(style.outer, [4, 6], weights.outerWidth / 2);
            configureStroke(style.inner, [4, 6], weights.innerWidth / 2);
            configureStroke(style.hair, [4, 6], weights.hairWidth / 2);
            break;
        case 'Gangfelt':
            configureStroke(style.outer, [5, 6], weights.outerWidth / 2);
            configureStroke(style.inner, [5, 6], weights.innerWidth / 2, '#D3D3D3');
            configureStroke(style.hair, [5, 6], weights.hairWidth / 2);
            break;
        case 'Gangveg':
        case 'Fortau':
            configureStroke(style.outer, [8, 6], weights.outerWidth / 2);
            configureStroke(style.inner, [8, 6], weights.innerWidth / 2);
            configureStroke(style.hair, [8, 6], weights.hairWidth / 2);
            break;
        case 'Trapp':
            const width = highlight || selected ? 10 : 7;
            configureStroke(style.outer, [3, 4], width, undefined, 'butt');
            configureStroke(style.inner, [3, 4], width, undefined, 'butt');
            configureStroke(style.hair, [3, 4], width, undefined, 'butt');
            break;
    }
    return [style.outer, style.inner, ...(highlight || selected ? [style.hair] : [])];
}

function configureStroke(
    style: Style,
    lineDash: number[],
    width: number,
    color?: string,
    lineCap?: CanvasLineCap
): void {
    const stroke = style.getStroke();
    if (!stroke) return;

    stroke.setLineDash(lineDash);
    stroke.setWidth(width);
    if (color) stroke.setColor(color);
    if (lineCap) stroke.setLineCap(lineCap);
}

export function lineStyle(
    feature: Feature<Geometry>,
    resolution: number,
    direction: string,
    highlight = false,
    selected = false,
    arrows = false
): Style[] {
    const lineStyle = getLineStyle(
        highlight,
        selected,
        feature.get(FeatureRoadType),
        feature
    );
    const showDirectionalArrows = feature.get(FeatureShowDirectionalArrows);
    const arrowStyles: Style[] = [];

    const geometry = feature.getGeometry();
    if (arrows && showDirectionalArrows && isArrowGeometry(geometry)) {
        const lineGeometries = toLineGeometries(geometry);
        const arrowStroke = new Stroke({
            color: setAlpha(feature.get(FeatureColorKey), 0.65),
            width: 2,
        });
        const highLightStroke = new Stroke({
            color: setAlpha('rgba(0,0,0,0)', 0.65),
            width: 1,
        });

        const arrowFill = new Fill({ color: 'rgba(0,0,0,0)' });
        const highLightArrowFill = new Fill({ color: '#ffffff' });
        const turn: boolean =
            direction !== 'geometri' && feature.get(FeatureDirection) === 'MOT';
        let totalLength = 0;
        let lastSegment: [Coordinate, Coordinate] | null = null;
        // Resolution is tied to zoom level. We use this to generate fewer arrows on high zoom levels.
        // Adding one to normalize results to some degree/less change per zoom level.
        const lengthBetweenArrows = 35 * (resolution + 1);
        let lengthSinceLastArrow = 0;
        lineGeometries.forEach(lineGeometry => {
            lineGeometry.forEachSegment((start, end) => {
                lastSegment = [start, end];
                totalLength += lengthBetweenPoints(start, end);
            });
        });

        // At the closest two zoom levels, we always put at least one arrow in each segment.
        if (resolution < 0.2 && lengthBetweenArrows > totalLength && lastSegment) {
            const start = lastSegment[0];
            const end = lastSegment[1];

            arrowStyles.push(
                new Style({
                    fill: highlight ? highLightArrowFill : arrowFill,
                    geometry: createArrow(start, end, turn),
                    stroke: highlight ? highLightStroke : arrowStroke,
                    zIndex: 4,
                })
            );
        } else {
            lineGeometries.forEach(lineGeometry => {
                lineGeometry.forEachSegment((start, end) => {
                    lengthSinceLastArrow += lengthBetweenPoints(start, end);
                    if (lengthSinceLastArrow < lengthBetweenArrows) return;
                    else lengthSinceLastArrow = 0;

                    arrowStyles.push(
                        new Style({
                            fill: highlight ? highLightArrowFill : arrowFill,
                            geometry: createArrow(start, end, turn),
                            stroke: highlight ? highLightStroke : arrowStroke,
                            zIndex: 4,
                        })
                    );
                });
            });
        }
    }

    function isArrowGeometry(
        geometry: unknown
    ): geometry is LineString | MultiLineString {
        return geometry instanceof LineString || geometry instanceof MultiLineString;
    }

    function toLineGeometries(geometry: LineString | MultiLineString): LineString[] {
        return geometry instanceof MultiLineString
            ? geometry.getLineStrings()
            : [geometry];
    }

    function createArrow(start: Coordinate, end: Coordinate, turn: boolean): Polygon {
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation =
            Math.atan2(dy, dx) - degreesToRadians(90) + degreesToRadians(turn ? 180 : 0);
        const newArrow = new Polygon([
            [
                [0, 0],
                [5, -5],
                [0, 10],
                [-5, -5],
                [0, 0],
            ],
        ]);

        newArrow.translate(start[0], start[1]);
        newArrow.rotate(rotation, [start[0], start[1]]);
        newArrow.scale(resolution, resolution, [start[0], start[1]]);

        return newArrow;
    }

    function degreesToRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    function lengthBetweenPoints(point_1: Coordinate, point_2: Coordinate): number {
        return Math.sqrt(
            Math.pow(point_2[0] - point_1[0], 2) + Math.pow(point_2[1] - point_1[1], 2)
        );
    }

    return lineStyle.concat(arrowStyles);
}
